import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, CircleDot, CheckCircle2, MinusCircle, Loader2, Lock } from "lucide-react";

const STATUS_META = {
  pending: { label: "Not started", cls: "bg-[#F3F4F6] text-[#595D5A]", Icon: CircleDot },
  in_progress: { label: "In progress", cls: "bg-[#FEF3C7] text-[#92600A]", Icon: Loader2 },
  done: { label: "Done", cls: "bg-[#DCFCE7] text-[#166534]", Icon: CheckCircle2 },
  na: { label: "N/A", cls: "bg-[#F3F4F6] text-[#9aa]", Icon: MinusCircle },
};

// scope: "client" or "staff". basePath e.g. "/intake/compliance" or `/staff/cases/${caseId}/compliance`
export const ComplianceChecklist = ({ scope, basePath, testId = "compliance" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const load = () => {
    api.get(basePath)
      .then(({ data }) => setItems(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [basePath]);

  const canEdit = (item) => {
    if (item.who === "auto") return false;
    if (scope === "staff") return true;
    return item.who === "client" || item.who === "both";
  };

  const update = async (key, status) => {
    setSavingKey(key);
    try {
      const { data } = await api.post(`${basePath}/${key}`, { status });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    } finally { setSavingKey(null); }
  };

  const done = items.filter((i) => i.status === "done" || i.status === "na").length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid={testId}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-[#114A31]" />
          <h2 className="font-heading text-lg font-semibold text-[#1C1C1C]">S-Corp Compliance Checklist</h2>
        </div>
        <span className="text-sm text-[#595D5A]" data-testid={`${testId}-progress`}>{done}/{items.length} complete · {pct}%</span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-[#E6F0EB] overflow-hidden">
        <div className="h-full bg-[#114A31] transition-all" style={{ width: `${pct}%` }} />
      </div>

      {loading ? (
        <div className="mt-6 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#114A31]" /></div>
      ) : (
        <ul className="mt-4 space-y-2">
          {items.map((item) => {
            const meta = STATUS_META[item.status] || STATUS_META.pending;
            const editable = canEdit(item);
            return (
              <li key={item.key} data-testid={`${testId}-item-${item.key}`}
                className="flex items-center gap-3 rounded-xl border border-[#E5E5E5] px-4 py-3">
                <meta.Icon className={`h-4 w-4 ${item.status === "done" ? "text-[#166534]" : "text-[#595D5A]"} ${item.status === "in_progress" ? "animate-spin" : ""}`} />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-[#1C1C1C]">{item.label}</span>
                  {item.recurring && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#92600A] bg-[#FEF3C7] px-1.5 py-0.5 rounded-full">recurring</span>}
                </div>
                {editable ? (
                  <Select value={item.status} onValueChange={(v) => update(item.key, v)} disabled={savingKey === item.key}>
                    <SelectTrigger className="w-36 h-8 rounded-lg border-[#E5E5E5] text-xs" data-testid={`${testId}-select-${item.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Not started</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="done">Done</SelectItem>
                      <SelectItem value="na">N/A</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-1 rounded-full ${meta.cls}`}>
                    {item.who === "auto" && <Lock className="h-3 w-3" />} {meta.label}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {scope === "client" && (
        <p className="mt-3 text-xs text-[#595D5A]">Items marked with a lock or handled by filings update automatically or by your specialist.</p>
      )}
    </div>
  );
};
